import { create } from "zustand";
import { ZoneId } from "@js-joda/core";
import { Locale } from "date-fns/types";
import { LEGACY_TZ_KEY } from "hooks/useTimezone";
import { UserDTO } from "../api/model/user/UserDTO";
import { CompanyDTO } from "../api/model/company/CompanyDTO";
import { BROWSER_ZONE_ID } from "../utils/time-utils";
import { getLocale } from "../utils/utils";

const DISPLAY_TZ_LOCALSTORAGE_KEY = "_user_displayTimezone";

export interface UserState {
  companyContext?: CompanyDTO;
  currentUser?: UserDTO;
  isCurrentUserLoaded: boolean;
  setCompanyContext: (company?: CompanyDTO) => void;
  setCurrentUser: (user?: UserDTO) => void;
  getLanguage: () => string;
  getLocaleDateFns: () => Locale;

  getDisplayTimezone: () => string;
  getDisplayZoneId: () => ZoneId;
  setDisplayTimezone: (timezone?: string) => void;
  getCompanyTimezone: () => string | undefined;
  getCompanyZoneId: () => ZoneId;
}

export const useUserState = create<UserState>()((set, get) => ({
  companyContext: undefined,
  currentUser: undefined,
  isCurrentUserLoaded: false,
  setCompanyContext: (company?: CompanyDTO) => set(() => ({ companyContext: company })),
  setCurrentUser: (user?: UserDTO) => {
    set(() => ({
      currentUser: user,
      isCurrentUserLoaded: true,
      companyContext: user?.accessibleCompanies.filter(c => user?.companyRef === c.companyReference)[0],
    }));
  },

  getLanguage: () => get().currentUser?.preferences.language || "en",
  getLocaleDateFns: () => {
    const locale = getLocale(get().getLanguage());
    return locale;
  },

  getDisplayTimezone: () => {
    return localStorage.getItem(DISPLAY_TZ_LOCALSTORAGE_KEY) || LEGACY_TZ_KEY;
  },
  getDisplayZoneId: () => safeParseZoneId(get().getDisplayTimezone()),
  setDisplayTimezone: (timezone?: string) => {
    localStorage.setItem(DISPLAY_TZ_LOCALSTORAGE_KEY, timezone || "");

    // Update a dummy field to trigger rerender
    set(() => ({
      companyContext: get().companyContext,
    }));
  },

  getCompanyTimezone: () => {
    return get().companyContext?.timezone;
  },
  getCompanyZoneId: () => safeParseZoneId(get().getCompanyTimezone()),
}));

export const safeParseZoneId = (tz: string | undefined): ZoneId => {
  if (!tz || tz === LEGACY_TZ_KEY || tz === "_BROWSER_") {
    return BROWSER_ZONE_ID;
  }

  return ZoneId.of(tz);
};

export const initUser = () => {
  console.log(`Initializing user context...`);
  const user = window.userInfo;
  useUserState.getState().setCurrentUser(user);
  console.log(`User context initialized`, useUserState.getState());
};
